// firebase.js
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, serverTimestamp, query, orderBy, limit, getDocs, doc, getDoc, updateDoc, runTransaction, increment, where, deleteDoc } from 'firebase/firestore';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { deriveEntitlement } from './domain/subscription';

// Firebase 프로젝트 설정 (Firebase Console에서 복사)
const firebaseConfig = {
    apiKey: "AIzaSyA3LZdQJ08lVtaggW4kzzdpL0zSe2CAuq4",
    authDomain: "fcfunnel.firebaseapp.com",
    projectId: "fcfunnel",
    storageBucket: "fcfunnel.firebasestorage.app",
    messagingSenderId: "78026166",
    appId: "1:78026166:web:c682e97894dc709cc6dd17",
    measurementId: "G-ZJ3KSVL3B7"
};

// Firebase 초기화
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Firestore 데이터베이스 인스턴스
export const db = getFirestore(app);

// FCM 메시징 인스턴스
export const messaging = getMessaging(app);

// Firebase Functions 인스턴스 (asia-northeast3 리전 사용)
export const functions = getFunctions(app, 'asia-northeast3');

// Firebase Storage 인스턴스
export const storage = getStorage(app);

// 개발 환경에서는 에뮬레이터 사용 (선택사항)
// if (process.env.NODE_ENV === 'development') {
//   try {
//     connectFirestoreEmulator(db, 'localhost', 8080);
//   } catch (error) {
//     console.log('Firestore emulator already connected');
//   }
// }

export default app;

// Firestore 컬렉션 및 필드 상수
export const COLLECTIONS = {
    DEVICES: 'devices',
    PUSH_TOKENS: 'push_tokens',
    PUSH_MESSAGES: 'push_messages',
    TRIAL_DAY_NOTIFICATIONS: 'trial_day_notifications',
    ARCADE_RANKINGS: 'arcade_rankings',
    AUDIOBOOKS: 'audiobooks'
};

export const DEVICE_STATUS = {
    TRIAL: 'trial',
    PENDING: 'pending',
    ACTIVE: 'active',
    REJECTED: 'rejected',
    SUBSCRIPTION_EXPIRED: 'subscription_expired'
};

// 유틸리티 함수들
export const createDeviceDocument = (deviceId, data) => ({
    deviceId,
    status: DEVICE_STATUS.PENDING,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...data
});

// 기기 링크(본/추가) 관리 헬퍼
export async function getDeviceLinkInfo(deviceId) {
    try {
        const ref = doc(db, COLLECTIONS.DEVICES, deviceId);
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        const data = snap.data();
        return {
            id: deviceId,
            status: data.status,
            isPrimary: !!data.isPrimary,
            isSecondary: !!data.isSecondary,
            linkedPrimaryId: data.linkedPrimaryId || null,
            linkedSecondaryId: data.linkedSecondaryId || null,
            userInfo: data.userInfo || null,
            nextBillingDate: data.nextBillingDate || null,
            entitlement: deriveEntitlement(deviceId, data)
        };
    } catch (e) {
        console.error('getDeviceLinkInfo 실패:', e);
        return null;
    }
}

export async function registerSecondaryDevice(primaryId, secondaryId) {
    // 사전조건: primary는 ACTIVE여야 함
    try {
        await runTransaction(db, async (trx) => {
            const pRef = doc(db, COLLECTIONS.DEVICES, primaryId);
            const sRef = doc(db, COLLECTIONS.DEVICES, secondaryId);

            const pSnap = await trx.get(pRef);
            if (!pSnap.exists()) throw new Error('본 기기 정보가 없습니다.');
            const pData = pSnap.data();
            if (pData.status !== DEVICE_STATUS.ACTIVE) throw new Error('활성 사용자만 추가기기 등록이 가능합니다.');
            if (pData.linkedSecondaryId && pData.linkedSecondaryId !== secondaryId) throw new Error('이미 추가기기가 등록되어 있습니다.');

            const sSnap = await trx.get(sRef);
            if (!sSnap.exists()) throw new Error('추가기기 정보가 없습니다.');
            const sData = sSnap.data();
            // 일반 REJECTED는 차단하되, 본 기기에서 해제된 추가기기(primary_unlinked)는 재등록 허용
            if (sData.status === DEVICE_STATUS.REJECTED && sData.revokeReason !== 'primary_unlinked') {
                throw new Error('제한된 기기는 등록할 수 없습니다.');
            }
            if (sData.linkedPrimaryId && sData.linkedPrimaryId !== primaryId) throw new Error('이미 다른 본 기기에 연결된 기기입니다.');

            // 링크 설정 및 동기 정보 반영
            const now = new Date().toISOString();
            trx.update(pRef, {
                isPrimary: true,
                linkedSecondaryId: secondaryId,
                updatedAt: now
            });
            trx.update(sRef, {
                isSecondary: true,
                linkedPrimaryId: primaryId,
                // 본 기기의 이용정보를 최소 반영
                status: DEVICE_STATUS.ACTIVE,
                userInfo: pData.userInfo || null,
                nextBillingDate: pData.nextBillingDate || null,
                // 재등록 시 제한 플래그 초기화
                secondaryRevokedAt: null,
                revokeReason: null,
                updatedAt: now
            });
        });
        return { success: true };
    } catch (e) {
        console.error('registerSecondaryDevice 실패:', e);
        return { success: false, message: e.message || '등록 실패' };
    }
}

export async function unregisterSecondaryDevice(primaryId, secondaryId) {
    try {
        await runTransaction(db, async (trx) => {
            const pRef = doc(db, COLLECTIONS.DEVICES, primaryId);
            const sRef = doc(db, COLLECTIONS.DEVICES, secondaryId);

            const pSnap = await trx.get(pRef);
            if (!pSnap.exists()) throw new Error('본 기기 정보가 없습니다.');
            const pData = pSnap.data();
            if (pData.linkedSecondaryId !== secondaryId) throw new Error('연결되지 않은 기기입니다.');

            const sSnap = await trx.get(sRef);
            if (!sSnap.exists()) throw new Error('추가기기 정보가 없습니다.');

            const now = new Date().toISOString();
            trx.update(pRef, {
                linkedSecondaryId: null,
                updatedAt: now
            });
            trx.update(sRef, {
                linkedPrimaryId: null,
                isSecondary: false,
                // 연결 해제 후 추가기기는 활성 권한이 없어야 함
                status: DEVICE_STATUS.REJECTED,
                secondaryRevokedAt: now,
                revokeReason: 'primary_unlinked',
                updatedAt: now
            });
        });
        return { success: true };
    } catch (e) {
        console.error('unregisterSecondaryDevice 실패:', e);
        return { success: false, message: e.message || '해제 실패' };
    }
}

// FCM 토큰 관리 함수들
export const requestNotificationPermission = async () => {
    try {
        const permission = await Notification.requestPermission();
        return permission === 'granted';
    } catch (error) {
        console.error('알림 권한 요청 실패:', error);
        return false;
    }
};

export const getFCMToken = async () => {
    try {
        // 서비스 워커 등록 확인
        if ('serviceWorker' in navigator) {
            await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        }

        const token = await getToken(messaging, {
            vapidKey: 'BKcGVErrCTAdjAIs6AMxn7y8y7J_lBGvYEX39fYRJ0sVI6w43Hu8ExHRfJ1MsOPyeJoCy3eqqYzYAEvbfgcDFWI'
        });
        return token;
    } catch (error) {
        console.error('FCM 토큰 가져오기 실패:', error);
        return null;
    }
};

export const setupMessageListener = (onMessageReceived) => {
    onMessage(messaging, (payload) => {
        try {
            if (payload?.data?.intent === 'open_ai_birthday') {
                console.log('[FCM] intent=open_ai_birthday → AI비서 생일 탭 오픈 요청');
                window.dispatchEvent(new CustomEvent('openAssistant', { detail: { tab: 'birthday' } }));
            }
            if (payload?.data?.intent === 'open_ai_todo') {
                console.log('[FCM] intent=open_ai_todo → AI비서 할일 탭 오픈 요청');
                window.dispatchEvent(new CustomEvent('openAssistant', { detail: { tab: 'todo' } }));
            }
        } catch (_) { }
        onMessageReceived(payload);
    });
};

// Firebase Functions 호출 함수들
export const sendPushNotification = httpsCallable(functions, 'sendPushNotification');
export const registerFCMToken = httpsCallable(functions, 'registerFCMToken');
export const processScheduledMessages = httpsCallable(functions, 'processScheduledMessages');
export const setBirthdayReminder = httpsCallable(functions, 'setBirthdayReminder');
export const setTodoReminder = httpsCallable(functions, 'setTodoReminder');
export const setFollowUpReminder = httpsCallable(functions, 'setFollowUpReminder');
export const sendApprovalEmail = httpsCallable(functions, 'sendApprovalEmail');
export const verifyAdminPassword = httpsCallable(functions, 'verifyAdminPassword');

// Google Calendar Functions

// 앱 공유 프로모션 Functions
export const trackShareReferral = httpsCallable(functions, 'trackShareReferral');
export const registerShareParticipant = httpsCallable(functions, 'registerShareParticipant');
export const getShareStats = httpsCallable(functions, 'getShareStats');
export const getAllShareStats = httpsCallable(functions, 'getAllShareStats');

// FCM 토큰을 서버에 등록하는 함수
export const registerTokenToServer = async (deviceId, status = 'active') => {
    try {
        const token = await getFCMToken();
        if (!token) {
            console.error('FCM 토큰을 가져올 수 없습니다.');
            return false;
        }

        console.log('FCM 토큰 등록 시도:', {
            deviceId: deviceId,
            deviceIdType: typeof deviceId,
            deviceIdLength: deviceId ? deviceId.length : 0,
            status: status,
            tokenLength: token.length
        });

        const result = await registerFCMToken({
            token: token,
            deviceId: deviceId,
            status: status
        });

        console.log('FCM 토큰 등록 결과:', result.data);

        if (result.data.success) {
            console.log('FCM 토큰이 서버에 등록되었습니다.');
            return true;
        } else {
            console.error('FCM 토큰 등록 실패:', result.data.message);
            return false;
        }
    } catch (error) {
        console.error('FCM 토큰 등록 중 오류:', error);
        console.error('오류 상세:', {
            name: error.name,
            message: error.message,
            code: error.code,
            details: error.details
        });
        return false;
    }
};

// Arcade Ranking APIs
export async function submitArcadeScore(name, score, gameType = 'falling-dots') {
    try {
        const ref = collection(db, COLLECTIONS.ARCADE_RANKINGS);
        await addDoc(ref, { 
            name: (name || 'NONAME').slice(0, 12), 
            score: Number(score) || 0, 
            gameType: gameType,
            createdAt: serverTimestamp() 
        });
        return true;
    } catch (e) {
        console.error('랭킹 제출 실패:', e);
        return false;
    }
}

export async function loadTopArcadeScores(topN = 10, gameType = 'falling-dots') {
    try {
        const ref = collection(db, COLLECTIONS.ARCADE_RANKINGS);
        const q = query(
            ref, 
            where('gameType', '==', gameType),
            orderBy('score', 'desc'), 
            orderBy('createdAt', 'asc'), 
            limit(topN)
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...(d.data()) }));
    } catch (e) {
        console.error('랭킹 로드 실패:', e);
        return [];
    }
}

// AudioBook APIs
// 업로드: 새 문서로 보관 저장 (덮어쓰기 없음)
export async function uploadAudiobook(file, title) {
    try {
        // 새 파일 업로드
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        const storagePath = `audiobooks/${fileName}`;
        const storageRef = ref(storage, storagePath);

        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        // Firestore 문서 생성 (보관용)
        const audiobookData = {
            title: title || file.name.replace(/\.[^/.]+$/, ''),
            fileName: file.name,
            storagePath,
            downloadURL,
            fileSize: file.size,
            createdAt: serverTimestamp(),
            createdBy: 'admin', // 관리자 고정
            playCount: 0,
            lastPlayedAt: null,
            isActive: false
        };

        const colRef = collection(db, COLLECTIONS.AUDIOBOOKS);
        const docRef = await addDoc(colRef, audiobookData);
        const saved = await getDoc(docRef);
        return { success: true, data: { id: docRef.id, ...saved.data() } };
    } catch (e) {
        console.error('오디오북 업로드 실패:', e);
        return { success: false, error: e.message || '업로드 실패' };
    }
}

// 현재 활성 오디오북 가져오기
export async function getActiveAudiobook() {
    try {
        const colRef = collection(db, COLLECTIONS.AUDIOBOOKS);
        // 인덱스 없이 동작하도록 orderBy 제거
        const qy = query(colRef, where('isActive', '==', true), limit(1));
        const snap = await getDocs(qy);
        if (snap.empty) return null;
        const d = snap.docs[0];
        return { id: d.id, ...d.data() };
    } catch (e) {
        console.error('오디오북 로드 실패:', e);
        return null;
    }
}

// 특정 오디오북 삭제
export async function deleteAudiobookById(audiobookId) {
    try {
        const targetRef = doc(db, COLLECTIONS.AUDIOBOOKS, audiobookId);
        const targetSnap = await getDoc(targetRef);
        if (!targetSnap.exists()) return { success: true };
        const data = targetSnap.data();

        if (data?.storagePath) {
            try {
                const fileRef = ref(storage, data.storagePath);
                await deleteObject(fileRef);
            } catch (e) { console.warn('파일 삭제 실패:', e); }
        }

        await deleteDoc(targetRef);

        return { success: true };
    } catch (e) {
        console.error('오디오북 삭제 실패:', e);
        return { success: false, error: e.message || '삭제 실패' };
    }
}

// 활성 오디오북 설정
export async function setActiveAudiobook(audiobookId) {
    try {
        await runTransaction(db, async (trx) => {
            // 기존 활성 문서 off
            const colRef = collection(db, COLLECTIONS.AUDIOBOOKS);
            const qy = query(colRef, where('isActive', '==', true), limit(10));
            const activeSnap = await getDocs(qy);
            activeSnap.forEach(docSnap => {
                trx.update(doc(db, COLLECTIONS.AUDIOBOOKS, docSnap.id), { isActive: false });
            });

            // 대상 on
            const targetRef = doc(db, COLLECTIONS.AUDIOBOOKS, audiobookId);
            trx.update(targetRef, { isActive: true });
        });
        return { success: true };
    } catch (e) {
        console.error('활성 오디오북 설정 실패:', e);
        return { success: false, error: e.message || '설정 실패' };
    }
}

// 오디오북 목록 조회 (최신순)
export async function listAudiobooks(topN = 20) {
    try {
        const colRef = collection(db, COLLECTIONS.AUDIOBOOKS);
        const qy = query(colRef, orderBy('createdAt', 'desc'), limit(topN));
        const snap = await getDocs(qy);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
        console.error('오디오북 목록 로드 실패:', e);
        return [];
    }
}

// 오디오북 재생 카운트 증가 (활성 문서 대상으로)
export async function incrementAudiobookPlay() {
    try {
        const active = await getActiveAudiobook();
        if (!active?.id) return false;
        const targetRef = doc(db, COLLECTIONS.AUDIOBOOKS, active.id);
        await updateDoc(targetRef, { playCount: increment(1), lastPlayedAt: new Date().toISOString() });
        return true;
    } catch (e) {
        console.error('오디오북 재생 카운트 증가 실패:', e);
        return false;
    }
}

// 오디오북 좋아요 카운트 증가 (활성 문서 대상으로)
export async function incrementAudiobookLike(deviceId, userName) {
    try {
        const active = await getActiveAudiobook();
        if (!active?.id) return false;
        
        const targetRef = doc(db, COLLECTIONS.AUDIOBOOKS, active.id);
        const likesRef = collection(db, COLLECTIONS.AUDIOBOOKS, active.id, 'likes');
        
        // 이미 좋아요를 눌렀는지 확인
        const existingLikesQuery = query(likesRef, where('deviceId', '==', deviceId));
        const existingSnap = await getDocs(existingLikesQuery);
        
        if (!existingSnap.empty) {
            // 이미 좋아요를 눌렀으면 중복 방지
            return { success: false, alreadyLiked: true };
        }
        
        // 좋아요 서브컬렉션에 추가
        await addDoc(likesRef, {
            deviceId,
            userName: userName || '익명',
            likedAt: serverTimestamp()
        });
        
        // 좋아요 카운트 증가
        await updateDoc(targetRef, { 
            likeCount: increment(1), 
            lastLikedAt: new Date().toISOString() 
        });
        
        return { success: true };
    } catch (e) {
        console.error('오디오북 좋아요 카운트 증가 실패:', e);
        return { success: false, error: e.message };
    }
}

// 오디오북 좋아요 목록 조회
export async function getAudiobookLikes(audiobookId) {
    try {
        const likesRef = collection(db, COLLECTIONS.AUDIOBOOKS, audiobookId, 'likes');
        const likesQuery = query(likesRef, orderBy('likedAt', 'desc'));
        const snap = await getDocs(likesQuery);
        return snap.docs.map(d => ({
            id: d.id,
            ...d.data(),
            likedAt: d.data().likedAt?.toDate ? d.data().likedAt.toDate().toISOString() : d.data().likedAt
        }));
    } catch (e) {
        console.error('좋아요 목록 조회 실패:', e);
        return [];
    }
}
